import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import gravityLogo from '../Image/gravity_logo.png';
import { PICK6_RULE, optionNumber, optionPrize } from '../utils/pick6';

// The Pick 6 coupon in the same receipt look as the other products' coupons
// (Transactions viewer, mobile print). The winning numbers have no heading, and
// the customer's matches are solid black — on the web only; the mobile print
// leaves them plain (the user's choice). Styles are inline because the same
// markup is written into a separate print window, where index.css doesn't exist.
const ARIAL = 'Arial, Helvetica, sans-serif';
const S = {
  paper: { width: 300, boxSizing: 'border-box', margin: '0 auto', padding: '18px 16px 16px', background: '#fff', color: '#000', textAlign: 'center', fontFamily: "'Courier New', Courier, monospace" },
  logo: { width: 90, height: 'auto', display: 'block', margin: '0 auto 6px' },
  title: { fontFamily: ARIAL, fontSize: 22, fontWeight: 800, letterSpacing: 0.3 },
  subtitle: { fontFamily: ARIAL, fontSize: 13, marginTop: 2 },
  divider: { borderTop: '2px dashed #000', margin: '10px 0' },
  details: { textAlign: 'left', fontSize: 12, lineHeight: 1.7 },
  row: { display: 'flex', whiteSpace: 'nowrap' },
  key: { width: 92, flexShrink: 0 },
  sep: { width: 14, flexShrink: 0 },
  val: { overflow: 'hidden', textOverflow: 'ellipsis' },
  label: { fontFamily: ARIAL, fontSize: 15, fontWeight: 800, marginTop: 4 },
  picks: { display: 'flex', justifyContent: 'center', gap: 6, margin: '10px 0 12px' },
  pick: { width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9, background: '#000', color: '#fff', fontFamily: ARIAL, fontSize: 19, fontWeight: 800 },
  product: { fontFamily: ARIAL, fontSize: 16, fontWeight: 800 },
  rule: { fontSize: 11, marginTop: 8, letterSpacing: 0.3 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6, margin: '12px 0 14px' },
  cell: { height: 48, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, border: '2px solid #000', borderRadius: 7, fontFamily: ARIAL },
  cellWin: { background: '#000', color: '#fff' },
  num: { fontSize: 17, fontWeight: 800, lineHeight: 1 },
  prize: { fontSize: 9.5, fontWeight: 700, lineHeight: 1 },
  qr: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 4 },
  qrText: { fontSize: 10, marginTop: 6, letterSpacing: 0.3 },
  footer: { fontFamily: ARIAL, fontSize: 16, fontWeight: 800, marginTop: 4 },
};

const pad2 = (n) => String(n).padStart(2, '0');
// Printed prizes are what is paid: the base prize × the Factor stored on the sale.
const paid = (base, factor) => `${(Math.round(Number(base) * (factor || 1) * 100) / 100).toLocaleString()} P`;

export default function Pick6Coupon({ txnId, storeSeq, storeCode, timestamp, userName, storeName, productName, picks, options, factor, logoSrc = gravityLogo }) {
  const d = timestamp ? new Date(timestamp) : new Date();
  const shown = [...(picks || [])]; // in the order the customer chose them
  const picked = new Set(shown);
  const details = [
    ['Date', `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`],
    ['Time', `${pad2(d.getHours())}:${pad2(d.getMinutes())}`],
    ['User Name', userName],
    ['Sequence No', storeSeq != null ? `#${storeCode || ''}${storeSeq}` : ''],
    ['Card No', txnId],
    ['Location', storeName],
  ].filter(([, v]) => v);

  return (
    <div style={S.paper}>
      <img src={logoSrc} alt="" style={S.logo} />
      <div style={S.title}>Instant Rewards</div>
      <div style={S.subtitle}>With Every Fragrance</div>
      <div style={S.divider} />
      <div style={S.details}>
        {details.map(([k, v]) => (
          <div key={k} style={S.row}>
            <span style={S.key}>{k}</span><span style={S.sep}>:</span><span style={S.val}>{v}</span>
          </div>
        ))}
      </div>
      <div style={S.divider} />
      <div style={S.label}>Your selected numbers</div>
      <div style={S.picks}>
        {shown.map(n => <div key={n} style={S.pick}>{n}</div>)}
      </div>
      <div style={S.product}>{productName}</div>
      <div style={S.rule}>{PICK6_RULE}</div>
      <div style={S.grid}>
        {(options || []).map(o => {
          const n = optionNumber(o);
          return (
            <div key={n} style={picked.has(n) ? { ...S.cell, ...S.cellWin } : S.cell}>
              <div style={S.num}>{n}</div>
              <div style={S.prize}>{paid(optionPrize(o), factor)}</div>
            </div>
          );
        })}
      </div>
      {txnId && (
        <div style={S.qr}>
          <QRCodeSVG value={txnId} size={112} level="M" />
          <div style={S.qrText}>{txnId}</div>
        </div>
      )}
      <div style={S.divider} />
      <div style={S.footer}>Thank you !!!</div>
    </div>
  );
}

// Opens the coupon in its own window and prints it (the web app's convention:
// printing from a new window keeps the sidebar and page styles out). Call it
// straight from a click handler — a window opened after an await is blocked.
// Returns false when the browser blocked the window.
export function printPick6Coupon(coupon) {
  const w = window.open('', '_blank', 'width=380,height=860');
  if (!w) return false;
  const logoSrc = new URL(gravityLogo, window.location.href).href;
  const body = renderToStaticMarkup(<Pick6Coupon {...coupon} logoSrc={logoSrc} />);
  const title = String(coupon.txnId || 'Coupon').replace(/[<>&"]/g, '');
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>`
    + '<style>@page{margin:6mm}body{margin:0;padding:12px 0;background:#fff}</style></head>'
    + `<body>${body}</body></html>`);
  w.document.close();
  const go = () => { w.focus(); w.print(); };
  const img = w.document.images[0];
  if (img && !img.complete) {
    img.onload = go;
    img.onerror = go;
  } else {
    w.setTimeout(go, 50);
  }
  return true;
}
